import { useState, useEffect } from 'react';
import api from '../services/api';

interface EditShipmentModalProps {
  order: any;
  onClose: () => void;
  onSaved: () => void;
}

interface DestinationData {
  company: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  contactName: string;
  phone: string;
}

interface ShipmentItem {
  description: string;
  qty: number;
  weight: number;
  length: number;
  width: number;
  height: number;
  freightClass: number;
  packagingType: string;
}

const FREIGHT_CLASSES = [50, 55, 60, 65, 70, 77.5, 85, 92.5, 100, 110, 125, 150, 175, 200, 250, 300, 400, 500];
const PACKAGING_TYPES = ['pallet', 'crate', 'drum', 'bundle'];
const SHIP_TYPES = ['business dock', 'residential', 'business no dock'];

function parseShippingAddress(order: any): DestinationData {
  let addr: any = {};

  if (order.shipping_address) {
    if (typeof order.shipping_address === 'string') {
      try {
        addr = JSON.parse(order.shipping_address);
      } catch {
        addr = {};
      }
    } else {
      addr = order.shipping_address;
    }
  }

  return {
    company: addr.company || addr.companyName || order.dealer_name || '',
    address: addr.address || addr.street || addr.address1 || '',
    city: addr.city || '',
    state: addr.state || '',
    zip: addr.zip || addr.zipCode || addr.postalCode || '',
    contactName: addr.contactName || addr.contact || addr.name || '',
    phone: addr.phone || addr.phoneNumber || '',
  };
}

function createDefaultItem(): ShipmentItem {
  return {
    description: 'V-Track Motorized Screen Systems',
    qty: 1,
    weight: 0,
    length: 0,
    width: 0,
    height: 0,
    freightClass: 85,
    packagingType: 'pallet',
  };
}

export default function EditShipmentModal({ order, onClose, onSaved }: EditShipmentModalProps) {
  const [destination, setDestination] = useState<DestinationData>(() => parseShippingAddress(order));
  const [shipType, setShipType] = useState('business dock');
  const [liftgate, setLiftgate] = useState(false);
  const [insideDelivery, setInsideDelivery] = useState(false);
  const [items, setItems] = useState<ShipmentItem[]>([createDefaultItem()]);
  const [pickupDate, setPickupDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notices, setNotices] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    // Pre-fill accessorials from order data if available
    if (order.liftgate || order.accessorials?.includes('liftgate')) {
      setLiftgate(true);
    }
    if (order.inside_delivery || order.accessorials?.includes('inside_delivery')) {
      setInsideDelivery(true);
    }
    if (order.ship_type || order.shipType) {
      setShipType(order.ship_type || order.shipType || 'business dock');
    }
  }, [order]);

  const updateDestination = (field: keyof DestinationData, value: string) => {
    setDestination(prev => ({ ...prev, [field]: value }));
  };

  const updateItem = (index: number, field: keyof ShipmentItem, value: string | number) => {
    setItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addItem = () => {
    setItems(prev => [...prev, createDefaultItem()]);
  };

  const removeItem = (index: number) => {
    if (items.length <= 1) return;
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    setError('');
    setNotices([]);
    setSuccessMessage('');
    setSaving(true);

    try {
      const accessorials: string[] = [];
      if (liftgate) accessorials.push('liftgate');
      if (insideDelivery) accessorials.push('inside_delivery');

      const data = {
        destination: {
          ...destination,
          shipType,
          accessorials,
        },
        items: items.map(item => ({
          description: item.description,
          qty: Number(item.qty),
          weight: Number(item.weight),
          length: Number(item.length),
          width: Number(item.width),
          height: Number(item.height),
          freightClass: Number(item.freightClass),
          packagingType: item.packagingType,
        })),
        pickupDate: pickupDate || undefined,
        orderId: order.id,
      };

      const response = await api.editFVShipment(order.freightview_shipment_id || order.freightviewShipmentId, data);

      if (response.notices && response.notices.length > 0) {
        setNotices(response.notices);
      }

      if (response.quotes) {
        setSuccessMessage('Quotes re-rated successfully.');
      }

      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update shipment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 rounded-t-xl z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">Edit Shipment</h2>
              <p className="text-gray-400 text-sm">
                {order.order_number || order.orderNumber} — FreightView #{order.freightview_shipment_id || order.freightviewShipmentId}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-4 space-y-5">
          {/* Error */}
          {error && (
            <div className="bg-red-900/40 border border-red-700 rounded-lg px-4 py-3">
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}

          {/* Notices */}
          {notices.map((notice, i) => (
            <div key={i} className="bg-yellow-900/40 border border-yellow-700 rounded-lg px-4 py-3">
              <p className="text-yellow-300 text-sm">{notice}</p>
            </div>
          ))}

          {/* Success */}
          {successMessage && (
            <div className="bg-green-900/40 border border-green-700 rounded-lg px-4 py-3">
              <p className="text-green-300 text-sm">{successMessage}</p>
            </div>
          )}

          {/* Destination Section */}
          <div>
            <h3 className="text-white font-semibold text-sm mb-3 uppercase tracking-wide">Destination</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-gray-400 text-xs mb-1">Company</label>
                <input
                  type="text"
                  value={destination.company}
                  onChange={e => updateDestination('company', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-gray-400 text-xs mb-1">Address</label>
                <input
                  type="text"
                  value={destination.address}
                  onChange={e => updateDestination('address', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-xs mb-1">City</label>
                <input
                  type="text"
                  value={destination.city}
                  onChange={e => updateDestination('city', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-400 text-xs mb-1">State</label>
                  <input
                    type="text"
                    value={destination.state}
                    onChange={e => updateDestination('state', e.target.value)}
                    maxLength={2}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1">ZIP</label>
                  <input
                    type="text"
                    value={destination.zip}
                    onChange={e => updateDestination('zip', e.target.value)}
                    maxLength={10}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-gray-400 text-xs mb-1">Contact Name</label>
                <input
                  type="text"
                  value={destination.contactName}
                  onChange={e => updateDestination('contactName', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-xs mb-1">Phone</label>
                <input
                  type="text"
                  value={destination.phone}
                  onChange={e => updateDestination('phone', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                />
              </div>
            </div>
          </div>

          {/* Ship Type & Accessorials */}
          <div>
            <h3 className="text-white font-semibold text-sm mb-3 uppercase tracking-wide">Shipping Options</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-gray-400 text-xs mb-1">Ship Type</label>
                <select
                  value={shipType}
                  onChange={e => setShipType(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                >
                  {SHIP_TYPES.map(type => (
                    <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={liftgate}
                    onChange={e => setLiftgate(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-indigo-500 focus:ring-indigo-500"
                  />
                  <span className="text-gray-300 text-sm">Liftgate</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={insideDelivery}
                    onChange={e => setInsideDelivery(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-indigo-500 focus:ring-indigo-500"
                  />
                  <span className="text-gray-300 text-sm">Inside Delivery</span>
                </label>
              </div>
            </div>
          </div>

          {/* Items Section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold text-sm uppercase tracking-wide">Items</h3>
              <button
                type="button"
                onClick={addItem}
                className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
              >
                + Add Item
              </button>
            </div>

            <div className="space-y-3">
              {items.map((item, index) => (
                <div key={index} className="bg-gray-750 border border-gray-600 rounded-lg p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-xs font-medium">Item {index + 1}</span>
                    {items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="text-red-400 hover:text-red-300 text-xs"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <label className="block text-gray-400 text-xs mb-1">Description</label>
                      <input
                        type="text"
                        value={item.description}
                        onChange={e => updateItem(index, 'description', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Qty</label>
                      <input
                        type="number"
                        min={1}
                        value={item.qty}
                        onChange={e => updateItem(index, 'qty', parseInt(e.target.value) || 1)}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Weight (lbs)</label>
                      <input
                        type="number"
                        min={0}
                        value={item.weight}
                        onChange={e => updateItem(index, 'weight', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Length (in)</label>
                      <input
                        type="number"
                        min={0}
                        value={item.length}
                        onChange={e => updateItem(index, 'length', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Width (in)</label>
                      <input
                        type="number"
                        min={0}
                        value={item.width}
                        onChange={e => updateItem(index, 'width', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Height (in)</label>
                      <input
                        type="number"
                        min={0}
                        value={item.height}
                        onChange={e => updateItem(index, 'height', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Freight Class</label>
                      <select
                        value={item.freightClass}
                        onChange={e => updateItem(index, 'freightClass', Number(e.target.value))}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                      >
                        {FREIGHT_CLASSES.map(fc => (
                          <option key={fc} value={fc}>{fc}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Packaging Type</label>
                      <select
                        value={item.packagingType}
                        onChange={e => updateItem(index, 'packagingType', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                      >
                        {PACKAGING_TYPES.map(pt => (
                          <option key={pt} value={pt}>{pt.charAt(0).toUpperCase() + pt.slice(1)}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pickup Date */}
          <div>
            <h3 className="text-white font-semibold text-sm mb-3 uppercase tracking-wide">Pickup Date</h3>
            <input
              type="date"
              value={pickupDate}
              onChange={e => setPickupDate(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors font-medium"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                  Saving...
                </span>
              ) : (
                'Update Shipment'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
